import { getChainFromVerifiedCredentialChain } from '../../../../utils/getChainFromVerifiedCredentialChain';
import type { Chain } from '../../../chain';
import type { WalletSchema } from '../../legacyWalletBook.types';

/**
 * Maps wallet book injected config chain identifiers to Chain type.
 */
const INJECTED_CHAIN_TO_CHAIN_MAP: Record<string, Chain> = {
  algo: 'ALGO',
  aptos: 'APTOS',
  bitcoin: 'BTC',
  btc: 'BTC',
  cosmos: 'COSMOS',
  eclipse: 'ECLIPSE',
  evm: 'EVM',
  flow: 'FLOW',
  sol: 'SOL',
  spark: 'SPARK',
  starknet: 'STARK',
  sui: 'SUI',
  ton: 'TON',
  tron: 'TRON',
};

type ExtractChainPrefixParams = {
  chainIdentifier: string;
};

/**
 * Extracts the chain prefix from a chain identifier string.
 * For example, "eip155:1" -> "eip155", "solana:..." -> "solana"
 */
const extractChainPrefix = ({
  chainIdentifier,
}: ExtractChainPrefixParams): string => {
  const colonIndex = chainIdentifier.indexOf(':');

  if (colonIndex === -1) {
    return chainIdentifier;
  }

  return chainIdentifier.substring(0, colonIndex);
};

/**
 * Maps chain name patterns found in wallet keys to Chain type.
 * These are common suffixes or embedded chain identifiers in wallet keys.
 */
const WALLET_KEY_CHAIN_PATTERNS: Array<{ chain: Chain; pattern: string }> = [
  { chain: 'SOL', pattern: 'sol' },
  { chain: 'SOL', pattern: 'solana' },
  { chain: 'BTC', pattern: 'btc' },
  { chain: 'BTC', pattern: 'bitcoin' },
  { chain: 'STARK', pattern: 'stark' },
  { chain: 'STARK', pattern: 'starknet' },
  { chain: 'SUI', pattern: 'sui' },
  { chain: 'APTOS', pattern: 'aptos' },
  { chain: 'COSMOS', pattern: 'cosmos' },
  { chain: 'FLOW', pattern: 'flow' },
  { chain: 'ALGO', pattern: 'algo' },
  { chain: 'TRON', pattern: 'tron' },
  { chain: 'TON', pattern: 'ton' },
  { chain: 'SPARK', pattern: 'spark' },
  { chain: 'ECLIPSE', pattern: 'eclipse' },
];

type GetChainFromWalletKeyParams = {
  walletKey: string;
};

/**
 * Attempts to extract the chain from a wallet key by looking for embedded chain identifiers.
 *
 * @param params.walletKey - The wallet key identifier.
 * @returns The chain type if found, undefined otherwise.
 */
const getChainFromWalletKey = ({
  walletKey,
}: GetChainFromWalletKeyParams): Chain | undefined => {
  const lowerKey = walletKey.toLowerCase();

  for (const { chain, pattern } of WALLET_KEY_CHAIN_PATTERNS) {
    if (lowerKey.includes(pattern)) {
      return chain;
    }
  }

  return undefined;
};

type GetChainFromWalletSchemaParams = {
  wallet: WalletSchema;
  walletKey?: string;
};

/**
 * Gets the chain from a wallet book entry.
 * Checks injectedConfig first, then falls back to the chains array, then the wallet key.
 * If no chain can be determined, defaults to EVM.
 *
 * @param params.wallet - The wallet schema from the wallet book.
 * @param params.walletKey - The wallet key identifier (optional, used as last resort fallback).
 * @returns The chain type, defaults to EVM if no chain can be determined.
 */
export const getChainFromWalletSchema = ({
  wallet,
  walletKey,
}: GetChainFromWalletSchemaParams): Chain => {
  // First, check injectedConfig for the chain field
  if (wallet.injectedConfig && wallet.injectedConfig.length > 0) {
    const injectedChain = wallet.injectedConfig[0].chain?.toLowerCase();

    if (injectedChain && INJECTED_CHAIN_TO_CHAIN_MAP[injectedChain]) {
      return INJECTED_CHAIN_TO_CHAIN_MAP[injectedChain];
    }
  }

  // Fall back to checking the chains array
  if (wallet.chains && wallet.chains.length > 0) {
    const firstChain = wallet.chains[0];
    const chainPrefix = extractChainPrefix({ chainIdentifier: firstChain });

    try {
      return getChainFromVerifiedCredentialChain(chainPrefix);
    } catch {
      // If the chain prefix is not recognized, fall back to wallet key check
    }
  }

  // Last resort: try to extract chain from wallet key
  if (walletKey) {
    const chainFromKey = getChainFromWalletKey({ walletKey });

    if (chainFromKey) {
      return chainFromKey;
    }
  }

  // Default to EVM if no chain can be determined
  return 'EVM';
};
